import { useLocale } from 'next-intl'
import { useMemo } from 'react'
import { IMenuItem } from '@/api/restaurants/types/restaurant-types'
import { highlightMatch } from '@/features/restaurants/restaurant-detail/utils/highlight-match'
import { Locale, toLocalePriceString } from '@/setup/locales'
import { cn } from '@/utils/styles/cn'

interface IRestaurantDetailMenuItemProps {
    menuItem: IMenuItem
    currencySymbol: string
    className?: string
    style?: React.CSSProperties
    /** Active search query, occurrences get marked in name and description */
    highlightQuery?: string
}

export const RestaurantDetailMenuItem = ({
    menuItem,
    className,
    style,
    currencySymbol,
    highlightQuery,
}: IRestaurantDetailMenuItemProps) => {
    const locale = useLocale()

    const readablePrice = useMemo(
        () =>
            typeof menuItem.price === 'number'
                ? toLocalePriceString({
                      price: menuItem.price,
                      locale: locale as Locale,
                      currencySymbol,
                  })
                : null,
        [menuItem.price, locale, currencySymbol]
    )

    return (
        <div className={cn('flex flex-col gap-y-0.5 w-full', className)} style={style}>
            <div className="flex items-baseline gap-x-2">
                <p className="text-p-md font-semibold">
                    {highlightQuery ? highlightMatch(menuItem.name, highlightQuery) : menuItem.name}
                </p>

                {readablePrice && (
                    <span
                        aria-hidden
                        // Empty flex items align their bottom edge to the text baseline,
                        // so the dotted border renders as a leader line on the baseline
                        className="flex-1 min-w-4 border-b border-dotted border-border-primary"
                    />
                )}

                {readablePrice && (
                    <p className="text-p-md tabular-nums whitespace-nowrap">{readablePrice}</p>
                )}
            </div>

            {menuItem.description && (
                <p className="text-fg-tertiary text-p-sm">
                    {highlightQuery
                        ? highlightMatch(menuItem.description, highlightQuery)
                        : menuItem.description}
                </p>
            )}
        </div>
    )
}
